import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon, SearchIcon } from 'lucide-react';
import { roleGroupLabels, roles } from '../../data/roles';
import { RoleGroup } from '../../types/commission';
import { formatBps, formatCurrency } from '../../utils/commission';
import { OfficerPeriodResult } from '../../utils/officerPeriod';
export type GroupFilter = 'All' | RoleGroup;
interface PeopleTableProps {
  rows: OfficerPeriodResult[];
  group: GroupFilter;
  onGroupChange: (group: GroupFilter) => void;
  periodLabel: string;
}
type SortKey = 'name' | 'position' | 'units' | 'volume' | 'commission';
const groupTabs: GroupFilter[] = ['All', 'Production', 'Leadership', 'Operations'];
interface Flag {
  label: string;
  tone: string;
}
function flagsFor(row: OfficerPeriodResult): Flag[] {
  const flags: Flag[] = [];
  if (row.units === 0) flags.push({
    label: 'No production',
    tone: 'bg-slate-100 text-slate-600'
  });
  if (row.role.requiresScorecard && !row.scorecardMet) flags.push({
    label: 'Scorecard missed',
    tone: 'bg-red-50 text-red-700'
  });
  if (row.totals.capped) flags.push({
    label: 'Capped',
    tone: 'bg-slate-100 text-slate-700'
  });
  if (row.adjustment) flags.push({
    label: 'Adjusted',
    tone: 'bg-amber-50 text-amber-800'
  });
  if (row.overrideCount > 0) flags.push({
    label: `${row.overrideCount} override${row.overrideCount === 1 ? '' : 's'}`,
    tone: 'bg-amber-50 text-amber-800'
  });
  return flags;
}
function rateLabel(row: OfficerPeriodResult): string {
  if (row.periodRate !== null) return formatBps(row.periodRate);
  return row.step.perUnit ? `${formatCurrency(row.step.perUnit)} / ${row.role.incentiveNoun ?? 'unit'}` : '—';
}

/** Searchable, filterable list of everyone paid in the period — the entry point to each drill-down. */
export function PeopleTable({
  rows,
  group,
  onGroupChange,
  periodLabel
}: PeopleTableProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [roleId, setRoleId] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('commission');
  const [descending, setDescending] = useState(true);
  const rolesInGroup = roles.filter(role => group === 'All' || role.group === group);
  const effectiveRoleId = rolesInGroup.some(role => role.id === roleId) ? roleId : 'all';
  const groupCounts = useMemo(() => {
    const counts: Record<GroupFilter, number> = {
      All: rows.length,
      Production: 0,
      Leadership: 0,
      Operations: 0
    };
    rows.forEach(row => {
      counts[row.role.group] += 1;
    });
    return counts;
  }, [rows]);
  const visibleRows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = rows.filter(row => {
      if (group !== 'All' && row.role.group !== group) return false;
      if (effectiveRoleId !== 'all' && row.role.id !== effectiveRoleId) return false;
      if (!needle) return true;
      return row.officer.name.toLowerCase().includes(needle) || row.officer.email.toLowerCase().includes(needle) || row.role.name.toLowerCase().includes(needle);
    });
    const direction = descending ? -1 : 1;
    return [...filtered].sort((a, b) => {
      if (sortKey === 'name') return a.officer.name.localeCompare(b.officer.name) * direction;
      if (sortKey === 'position') return a.role.name.localeCompare(b.role.name) * direction;
      if (sortKey === 'units') return (a.units - b.units) * direction;
      if (sortKey === 'volume') return (a.volume - b.volume) * direction;
      return (a.totals.net - b.totals.net) * direction;
    });
  }, [rows, group, effectiveRoleId, query, sortKey, descending]);
  const totalCommission = visibleRows.reduce((sum, row) => sum + row.totals.net, 0);
  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setDescending(value => !value);
      return;
    }
    setSortKey(key);
    setDescending(key !== 'name' && key !== 'position');
  }
  const sortHeader = (key: SortKey, label: string, align: 'left' | 'right' = 'left') => <button type="button" onClick={() => handleSort(key)} aria-label={`Sort by ${label}`} className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider transition hover:text-brand-700 ${key === sortKey ? 'text-brand-700' : 'text-slate-500'} ${align === 'right' ? 'flex-row-reverse' : ''}`} data-id="element-87" data-mp="1beos1kmk24dd">
      {label}
      <span aria-hidden="true" className={key === sortKey ? '' : 'text-slate-300'} data-id="element-88" data-mp="sjhvwfr005x5">
        {key === sortKey ? descending ? '↓' : '↑' : '↕'}
      </span>
    </button>;
  return <section id="people" aria-labelledby="people-heading" className="rounded-xl border border-slate-200 bg-white shadow-card" data-id="element-89" data-mp="12e9unmwbgyti">
      <div className="space-y-4 border-b border-slate-200 p-6" data-id="element-90" data-mp="16f3i6f5ukblv">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between" data-id="element-91" data-mp="2du8966a9kd24">
          <h2 id="people-heading" className="text-base font-bold text-slate-900" data-id="element-92" data-mp="2hva8so83er4m">
            People · {periodLabel}
          </h2>
          <p className="text-sm text-slate-500" data-id="element-93" data-mp="6bceuqkckw80">Select anyone to open their full commission statement.</p>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-94" data-mp="3tmc2jvpigcjz">
          <div role="tablist" aria-label="Position group" className="flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1" data-id="element-95" data-mp="3on908j0x0kfp">
            {groupTabs.map(tab => {
            const isActive = tab === group;
            return <button key={tab} type="button" role="tab" aria-selected={isActive} onClick={() => onGroupChange(tab)} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition ${isActive ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`} data-id="element-96" data-mp="3eos314bdv9l7">
                  {tab === 'All' ? 'All' : roleGroupLabels[tab]}
                  <span className={`rounded-full px-1.5 text-xs tabular-nums ${isActive ? 'bg-brand-50 text-brand-700' : 'bg-slate-200 text-slate-500'}`} data-id="element-97" data-mp="1mzipecmmta6x">
                    {groupCounts[tab]}
                  </span>
                </button>;
          })}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row" data-id="element-98" data-mp="26qxir0a2w8ii">
            <label className="sr-only" htmlFor="people-position" data-id="element-99" data-mp="2aqxugd5sm5lv">
              Position
            </label>
            <select id="people-position" value={effectiveRoleId} onChange={event => setRoleId(event.target.value)} className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700" data-id="element-100" data-mp="1wry8xedku6tx">
              <option value="all" data-id="element-101" data-mp="qb31ekz678vx">All positions</option>
              {rolesInGroup.map(role => <option key={role.id} value={role.id} data-id="element-102" data-mp="irbw5w7wyxzh">
                  {role.name}
                </option>)}
            </select>
            <div className="relative" data-id="element-103" data-mp="ds8nm1sq69mr">
              <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" data-id="element-104" data-mp="rsjkpmkmokgd" />
              <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search name, email, or position" aria-label="Search people" className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 sm:w-72" data-id="element-105" data-mp="mrlxuwoob28j" />
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-106" data-mp="3ch62f9xf17kp">
        <table className="w-full min-w-[880px] text-left text-sm" data-id="element-107" data-mp="37jxlfco079l1">
          <thead data-id="element-108" data-mp="37je520e04t94">
            <tr className="border-b border-slate-200 bg-slate-50" data-id="element-109" data-mp="37htgcredc4j6">
              <th scope="col" className="px-6 py-3" data-id="element-110" data-mp="2mzf4potg29ou">
                {sortHeader('name', 'Person')}
              </th>
              <th scope="col" className="px-4 py-3" data-id="element-111" data-mp="2r1ii4izobocr">
                {sortHeader('position', 'Position')}
              </th>
              <th scope="col" className="px-4 py-3 text-right" data-id="element-112" data-mp="30xc22nfq2k4k">
                {sortHeader('units', 'Units', 'right')}
              </th>
              <th scope="col" className="px-4 py-3 text-right" data-id="element-113" data-mp="35vx3gq6pn9ul">
                {sortHeader('volume', 'Volume', 'right')}
              </th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-114" data-mp="39vxfy5a91az0">
                Rate
              </th>
              <th scope="col" className="px-4 py-3 text-right" data-id="element-115" data-mp="3jnld8mthlm8e">
                {sortHeader('commission', 'Commission', 'right')}
              </th>
              <th scope="col" className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-116" data-mp="3tm14qqwfexif">
                Status
              </th>
              <th scope="col" className="w-10 px-4 py-3" data-id="element-117" data-mp="1i08q9g1bnci9">
                <span className="sr-only" data-id="element-118" data-mp="1czl0exotx1pm">Open</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-119" data-mp="17zydfwj7873m">
            {visibleRows.length === 0 ? <tr data-id="element-120" data-mp="132g8sic7se1f">
                <td colSpan={8} className="px-6 py-14 text-center" data-id="element-121" data-mp="132ydm3cy00ky">
                  <p className="text-sm font-semibold text-slate-700" data-id="element-122" data-mp="20pylu5cws2b8">No people match these filters</p>
                  <p className="mt-1 text-sm text-slate-500" data-id="element-123" data-mp="1vq2d5st9nmpc">Try a different name or clear the position filter.</p>
                </td>
              </tr> : visibleRows.map(row => {
            const flags = flagsFor(row);
            const href = `/people/${row.officer.id}`;
            return <tr key={row.officer.id} onClick={() => navigate(href)} className="group cursor-pointer transition hover:bg-brand-50/50" data-id="element-124" data-mp="2jh7wihyf0x4e">
                    <th scope="row" className="px-6 py-3.5 font-normal" data-id="element-125" data-mp="1e41btp1ecy3p">
                      <span className="flex items-center gap-3" data-id="element-126" data-mp="1a2fsbkucxlkj">
                        <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-900 text-xs font-bold text-white" data-id="element-127" data-mp="1o1fi4vtlw6ia">
                          {row.officer.initials}
                        </span>
                        <span className="min-w-0" data-id="element-128" data-mp="1syz6iok0pzny">
                          <Link to={href} onClick={event => event.stopPropagation()} className="block font-semibold text-slate-900 group-hover:text-brand-700" data-id="element-129" data-mp="26qm4qtxiti9d">
                            {row.officer.name}
                          </Link>
                          <span className="block truncate text-xs text-slate-500" data-id="element-130" data-mp="2brtcwu3ksjgk">{row.officer.email}</span>
                        </span>
                      </span>
                    </th>
                    <td className="px-4 py-3.5" data-id="element-131" data-mp="2zctyqlrnv89l">
                      <span className="block font-medium text-slate-800" data-id="element-132" data-mp="2ldukm81rybro">{row.role.name}</span>
                      <span className="block text-xs text-slate-500" data-id="element-133" data-mp="2qcp4zefe48dw">{roleGroupLabels[row.role.group]}</span>
                    </td>
                    <td className="px-4 py-3.5 text-right" data-id="element-134" data-mp="2bmq6tbnu4wyf">
                      <span className="block font-semibold tabular-nums text-slate-900" data-id="element-135" data-mp="2gehosxsxducy">{row.units}</span>
                      <span className="block text-xs text-slate-500" data-id="element-136" data-mp="22ncv0cc8nwaj">{row.role.scope.toLowerCase()}</span>
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums text-slate-700" data-id="element-137" data-mp="27n90yh4hxxsq">{formatCurrency(row.volume)}</td>
                    <td className="px-4 py-3.5 text-right tabular-nums text-slate-700" data-id="element-138" data-mp="1spgl1nv1f7e0">{rateLabel(row)}</td>
                    <td className="px-4 py-3.5 text-right font-bold tabular-nums text-slate-900" data-id="element-139" data-mp="1xn8cyhvomti7">
                      {formatCurrency(row.totals.net)}
                    </td>
                    <td className="px-4 py-3.5" data-id="element-140" data-mp="19rwmo7s22n8p">
                      {flags.length > 0 ? <span className="flex flex-wrap gap-1.5" data-id="element-141" data-mp="14umwur0r89kc">
                          {flags.map(flag => <span key={flag.label} className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${flag.tone}`} data-id="element-142" data-mp="zw1vupdohluu">
                              {flag.label}
                            </span>)}
                        </span> : <span className="text-xs text-slate-400" data-id="element-143" data-mp="1ikqgda891p63">—</span>}
                    </td>
                    <td className="px-4 py-3.5" data-id="element-144" data-mp="c2jslhj7nr03">
                      <ChevronRightIcon className="h-4 w-4 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-brand-600" aria-hidden="true" data-id="element-145" data-mp="74s0onikg4vw" />
                    </td>
                  </tr>;
          })}
          </tbody>
          {visibleRows.length > 0 ? <tfoot data-id="element-146" data-mp="2xikyfcrm0ywu">
              <tr className="border-t border-slate-200 bg-slate-50" data-id="element-147" data-mp="2xj4f6q5ixh9a">
                <th scope="row" colSpan={5} className="px-6 py-4 text-left font-bold text-slate-900" data-id="element-148" data-mp="32hfomzrwppqz">
                  {visibleRows.length} {visibleRows.length === 1 ? 'person' : 'people'}
                </th>
                <td className="px-4 py-4 text-right font-bold tabular-nums text-brand-800" data-id="element-149" data-mp="3bhc5wgbyvmc0">
                  {formatCurrency(totalCommission)}
                </td>
                <td colSpan={2} data-id="element-150" data-mp="3gekrkqwbq8i0" />
              </tr>
            </tfoot> : null}
        </table>
      </div>
    </section>;
}